import { Popover as BasePopover } from '@base-ui/react/popover';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tour.module.css';

export type TourSide = 'top' | 'bottom' | 'left' | 'right';
export type TourAlign = 'start' | 'center' | 'end';

/** 目标元素：推荐用 `data-tour="xxx"` 这类稳定锚点，CSS Modules 类名带 hash 不能当锚点。 */
export type TourTarget = string | (() => Element | null);

export interface TourStep {
  target: TourTarget;
  title?: string;
  content: ReactNode;
  align?: TourAlign;
  side?: TourSide;
}

export interface TourProps {
  /** 是否处于引导中；页面自己决定何时开启（首次进入、点了「重新引导」等）。 */
  active: boolean;
  /** 最后一步按钮的文字；单步引导常改成「知道了」这类更贴切的措辞。默认「完成」。 */
  finishText?: string;
  /** 完成、跳过、按 Esc、点遮罩都会调用；调用方负责把 active 置回 false。 */
  onFinish: () => void;
  steps: readonly TourStep[];
}

interface HighlightRect {
  height: number;
  left: number;
  top: number;
  width: number;
}

/** 高亮框相对目标元素的外扩，让目标四周留出一圈呼吸位。 */
const HIGHLIGHT_PADDING = 4;
const HIGHLIGHT_RADIUS = 6;

/*
 * 气泡与高亮框的间距：34 = 指针圆点中心距卡片底缘的 34px（线 30 + 圆点中心偏移 4），
 * 圆点中心正好落在高亮框上缘（行顶边外 4px），圆点尖端碰到行、不压行内文字。
 */
const SIDE_OFFSET = 34;

function resolveTarget(target: TourTarget): Element | null {
  return typeof target === 'string' ? document.querySelector(target) : target();
}

function measureTarget(target: TourTarget): HighlightRect | null {
  const element = resolveTarget(target);
  if (!element) return null;
  const { height, left, top, width } = element.getBoundingClientRect();
  return {
    height: height + HIGHLIGHT_PADDING * 2,
    left: left - HIGHLIGHT_PADDING,
    top: top - HIGHLIGHT_PADDING,
    width: width + HIGHLIGHT_PADDING * 2,
  };
}

/*
 * 新手引导（Onboarding / Tour）：高亮挖空遮罩 + 跟随目标的气泡。
 *
 * 分工：遮罩是本地 SVG（圆角挖洞 + 拦截点击），气泡视觉也是本地组件与 token；
 * 只把「定位」交给 Base UI Popover —— 翻转、避让、跟随滚动都由它处理，
 * 我们不手写坐标。锚点是一个跟随目标矩形的高亮框，目标变化时它移动，气泡自动跟上。
 *
 * 范围：单页面内的一套步骤。不做跨路由编排、不做完成状态持久化。
 */
export function Tour({ active, finishText = '完成', onFinish, steps }: TourProps) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<HighlightRect | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const maskId = `tour-mask-${useId().replace(/:/g, '')}`;

  const step = steps[Math.min(index, steps.length - 1)];
  const last = index >= steps.length - 1;

  /* 换步骤时先把目标滚进视野；滚动会触发下面的测量更新。 */
  useEffect(() => {
    if (!active || !step) return;
    /* jsdom 没实现 scrollIntoView，用可选调用兜住测试环境。 */
    resolveTarget(step.target)?.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  }, [active, index, step]);

  /*
   * 目标挂载可能晚于引导开始（切 tab、模式切换会重挂载），
   * 所以先按帧重试，找到后持续跟随滚动、窗口变化与文档尺寸变化。
   */
  useEffect(() => {
    if (!active || !step) {
      setRect(null);
      return;
    }

    let frame = requestAnimationFrame(function measure() {
      const next = measureTarget(step.target);
      if (!next) {
        frame = requestAnimationFrame(measure);
        return;
      }
      setRect(next);
    });

    function update() {
      setRect(measureTarget(step.target));
    }

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    /* jsdom 没有 ResizeObserver，测试环境下跳过。 */
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(document.body);

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [active, step]);

  /* 气泡焦点：Tab 在气泡内循环，不跑到被遮住的内容里。 */
  const trapFocus = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !popupRef.current) return;
    const focusable = popupRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const lastItem = focusable[focusable.length - 1];
    if (!event.shiftKey && document.activeElement === lastItem) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      lastItem.focus();
    }
  }, []);

  function goNext() {
    if (last) {
      onFinish();
      return;
    }
    setIndex((current) => current + 1);
  }

  function goPrev() {
    setIndex((current) => Math.max(0, current - 1));
  }

  if (!active || !step) return null;

  return createPortal(
    <BasePopover.Root
      open
      /* 只认 Escape：点遮罩（outsidePress）不关闭，隐藏一律走「知道了」等按钮。 */
      onOpenChange={(open, details) => { if (!open && details.reason === 'escape-key') onFinish(); }}
    >
      <div
        aria-hidden="true"
        className={styles.anchor}
        ref={anchorRef}
        style={rect ? { height: `${rect.height}px`, left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px` } : { display: 'none' }}
      />
      {rect ? <svg aria-hidden="true" className={styles.overlay}>
        <defs>
          <mask id={maskId}>
            <rect fill="#fff" height="100%" width="100%" x="0" y="0" />
            <rect fill="#000" height={rect.height} rx={HIGHLIGHT_RADIUS} width={rect.width} x={rect.left} y={rect.top} />
          </mask>
        </defs>
        <rect className={styles.mask} height="100%" mask={`url(#${maskId})`} width="100%" x="0" y="0" />
      </svg> : null}
      <BasePopover.Portal>
        <BasePopover.Positioner
          align={step.align ?? 'start'}
          anchor={anchorRef}
          className={styles.positioner}
          side={step.side ?? 'bottom'}
          sideOffset={SIDE_OFFSET}
        >
          <BasePopover.Popup aria-label="新手引导" className={styles.popup} onKeyDown={trapFocus} ref={popupRef} role="dialog">
            {/*
             * 装饰星：左上、右下各一组（大黄星 + 小奶白星），路径与配色取自 Figma 装饰切图
             * （节点 12471-25827，#FFD568 / #FFF8E8 是切图原色，非主题色，故不走 token）。
             */}
            <svg aria-hidden="true" className={[styles.sparkle, styles.sparkleStart].join(' ')} fill="none" viewBox="0 0 25 31.689">
              <path d="M14.6003 1.85433C14.9648 1.10303 16.0352 1.10303 16.3997 1.85433L18.4522 6.08464C18.5502 6.28666 18.7133 6.44981 18.9154 6.54782L23.1457 8.6003C23.897 8.96483 23.897 10.0352 23.1457 10.3997L18.9154 12.4522C18.7133 12.5502 18.5502 12.7133 18.4522 12.9154L16.3997 17.1457C16.0352 17.897 14.9648 17.897 14.6003 17.1457L12.5478 12.9154C12.4498 12.7133 12.2867 12.5502 12.0846 12.4522L7.85433 10.3997C7.10303 10.0352 7.10303 8.96483 7.85433 8.6003L12.0846 6.54782C12.2867 6.44981 12.4498 6.28666 12.5478 6.08464L14.6003 1.85433Z" fill="#FFD568" />
              <path d="M5.46678 20.6903C5.68155 20.2086 6.35721 20.1868 6.60265 20.6536L7.98463 23.2821C8.05062 23.4076 8.15694 23.5072 8.28646 23.565L10.9987 24.7743C11.4804 24.989 11.5023 25.6647 11.0355 25.9101L8.40698 27.2921C8.28146 27.3581 8.1818 27.4644 8.12405 27.5939L6.91477 30.3062C6.7 30.7879 6.02435 30.8098 5.77891 30.3429L4.39693 27.7145C4.33094 27.5889 4.22462 27.4893 4.0951 27.4315L1.38282 26.2223C0.901121 26.0075 0.879273 25.3318 1.34609 25.0864L3.97458 23.7044C4.1001 23.6384 4.19976 23.5321 4.2575 23.4026L5.46678 20.6903Z" fill="#FFF8E8" />
            </svg>
            <svg aria-hidden="true" className={[styles.sparkle, styles.sparkleEnd].join(' ')} fill="none" viewBox="0 0 24.6914 32.6901">
              <path d="M14.2917 1.85433C14.6562 1.10303 15.7266 1.10303 16.0911 1.85433L18.1436 6.08464C18.2416 6.28666 18.4047 6.44981 18.6068 6.54782L22.8371 8.6003C23.5884 8.96483 23.5884 10.0352 22.8371 10.3997L18.6068 12.4522C18.7133 12.5502 18.5502 12.7133 18.4522 12.9154L16.0911 17.1457C15.7266 17.897 14.6562 17.897 14.2917 17.1457L12.2392 12.9154C12.1412 12.7133 11.9781 12.5502 11.7761 12.4522L7.54574 10.3997C6.79443 10.0352 6.79443 8.96483 7.54574 8.6003L11.776 6.54782C11.9781 6.44981 12.1412 6.28666 12.2392 6.08464L14.2917 1.85433Z" fill="#FFD568" />
              <path d="M4.46731 23.7226C4.68208 23.2409 5.35773 23.2191 5.60317 23.6859L6.63687 25.652C6.70287 25.7775 6.80919 25.8772 6.93871 25.9349L8.96746 26.8394C9.44916 27.0542 9.47101 27.7299 9.00419 27.9753L7.03811 29.009C6.91259 29.075 6.81293 29.1813 6.75519 29.3108L5.85066 31.3396C5.63589 31.8213 4.96024 31.8431 4.7148 31.3763L3.68109 29.4102C3.6151 29.2847 3.50878 29.1851 3.37926 29.1273L1.35051 28.2228C0.868803 28.008 0.846954 27.3324 1.31378 27.0869L3.27985 26.0532C3.40537 25.9872 3.50503 25.8809 3.56278 25.7514L4.46731 23.7226Z" fill="#FFF8E8" />
            </svg>
            {steps.length > 1 ? <span className={styles.counter}>{index + 1} / {steps.length}</span> : null}
            {step.title ? <p className={styles.title}>{step.title}</p> : null}
            <div className={styles.content}>{step.content}</div>
            <div className={styles.actions}>
              {steps.length > 1 ? <button className={styles.pill} onClick={onFinish} type="button">跳过</button> : null}
              {index > 0 ? <button className={styles.pill} onClick={goPrev} type="button">上一步</button> : null}
              <button className={styles.pill} onClick={goNext} type="button">{last ? finishText : '下一步'}</button>
            </div>
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>,
    document.body,
  );
}
